import { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core';
import { Formik, Form, Field } from 'formik';
import { useSelector } from 'react-redux';
import RoundedButton from 'components/Shared/RoundedButton';
import UnsavedChangesGuard from 'components/Shared/UnsavedChangesGuard';
import UserRoleDetailsSection from './shared/RoleAndPermissions/UserRoleDetailsSection';
import UserPermissionSection from './shared/RoleAndPermissions/UserPermissionSection';
import RemoveAccountModal from './shared/UserModals/RemoveAccountModal';
import AllowedAccountsSectionModal from './AllowedAccountsSection/widgets/AllowedAccountsModal';
import AllowedAccountsSection from './AllowedAccountsSection/AllowedAccountsSection';
import FormSection from '../../../Shared/FormSection';
import { ACCOUNT_TYPES } from '../../../Accounts/constants';
import CheckBoxOption from './shared/FormComponents/CheckBoxOption';

const useStyles = makeStyles((theme) => ({
  accountsContent: {
    flex: 1,
    padding: theme.spacing(0 , 0, 2.5, 0),
    overflowY: 'auto',
    background: 'white',
    height: '100%',
    minHeight: 0, // Allow shrinking in flex containers
    display: 'flex',
    flexDirection: 'column',
  },
  contentHeader: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    position: 'sticky',
    top: 0,
    zIndex: 101,
    backgroundColor: 'white',
    paddingTop: 20,
  },
  contentHeaderInfo: {
    flex: 1,
  },
  contentTitle: {
    fontSize: 24,
    fontWeight: 600,
    color: '#333',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(0),
  },
  contentSubtitle: {
    color: '#666',
    fontSize: 14,
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(2),
    marginLeft: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  removeButton: {
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#c82333',
      borderColor: '#bd2130',
      color: 'white',
    },
  },
  actionsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    zIndex: 100,
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(2, 4),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2, 2),
      gap: theme.spacing(1),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
    [theme.breakpoints.down('sm')]: {
      minWidth: 100,
      padding: theme.spacing(1, 2),
      fontSize: 13,
    },
  },
}));

const AccountContent = ({
                          currentAccount,
                          onRemoveAccount,
                          onSaveChanges,
                          onDirtyChange,
                          regularLayout = false,
                          totalAccountsCount = 0,
                          shouldResetForm = false,
                          onResetComplete,
                          backendErrors,
                          setBackendErrors,
                          formikRef,
                          getInitialValues,
                        }) => {
  const classes = useStyles();
  const { en: { titles } } = useSelector((state) => state.i18n);
  const {
    saveStatus,
    saveError,
    saveValidationErrors,
    originalManagedUser,
  } = useSelector((state) => state.userAccountManagement);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [openAllowedAccountsModal, toggleAllowedAccountsModal] = useState(false);

  // Update backend errors when validation errors change
  useEffect(() => {
    setBackendErrors({});

    const values = getInitialValues();
    formikRef.current.resetForm({
      values: values,
      initialValues: values,
    });
  }, [currentAccount.accountUUID]);

  // Update backend errors when validation errors change
  useEffect(() => {
    if (saveValidationErrors) {
      setBackendErrors(saveValidationErrors);
    } else {
      setBackendErrors({});
    }
  }, [saveValidationErrors]);


  const handleRemoveClick = () => {
    setIsRemoveModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsRemoveModalOpen(false);
    setIsRemoving(false);
  };

  const handleConfirmRemove = async () => {
    setIsRemoving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Call the parent's remove handler
      if (onRemoveAccount) {
        onRemoveAccount(currentAccount.accountUUID);
      }

      handleCloseModal();
    } catch (error) {
      setIsRemoving(false);
    }
  };

  return (
    <div className={classes.accountsContent} style={{ marginBottom: regularLayout ? 60 : 0 }}>
      <div className={classes.contentHeader}>
        {!regularLayout && <div className={classes.contentHeaderInfo}>
          <h2 className={classes.contentTitle}>
            {currentAccount?.accountBusinessName || currentAccount?.name || 'Account'}
          </h2>
          <p className={classes.contentSubtitle}>
            {titles.configureAccessPermissions}
          </p>
        </div>}

        {!regularLayout && <div className={classes.headerActions}>
          <RoundedButton
            variant="contained"
            className={classes.removeButton}
            onClick={handleRemoveClick}
            disabled={!currentAccount}
          >
            {titles.removeAccess}
          </RoundedButton>
          <RoundedButton
            type="submit"
            variant="contained"
            color="primary"
            form="account-form"
            disabled={!isDirty}
          >
            {titles.saveChanges}
          </RoundedButton>
        </div>}
      </div>

      <Formik
        innerRef={formikRef}
        key={`${currentAccount?.id || 'no-account'}`}
        initialValues={getInitialValues()}
        onSubmit={(values, { resetForm }) => {
          if (currentAccount) {
            // Ensure provider fields are empty strings when role is assistant
            const processedValues = {
              ...values,
              providerTitle: values.role === 'assistant' ? '' : (values.providerTitle || ''),
              providerNPINumber: values.role === 'assistant' ? '' : (values.providerNPINumber || ''),
            };
            onSaveChanges(processedValues, () => {
              resetForm({
                values: processedValues,
                initialValues: processedValues,
              });
            });
          }
        }}
        enableReinitialize
      >
        {({ dirty, ...formik }) => {
          // Notify parent component about dirty state changes
          useEffect(() => {
            if (onDirtyChange) {
              onDirtyChange(dirty);
            }
            // Update local dirty state for button
            setIsDirty(dirty);
          }, [dirty]);

          return (
            <UnsavedChangesGuard
              hasUnsavedChanges={dirty}
              title="Unsaved changes"
              message={`You have unsaved changes to ${currentAccount?.name} permissions.`}
              discardButtonText="Leave anyway"
              keepEditingButtonText="Keep editing"
            >
              <Form id="account-form">
                <Field name="role" type="text">
                  {({ form }) => (
                    <>
                      {
                        openAllowedAccountsModal ? <AllowedAccountsSectionModal
                            values={formik.values}
                            userUuid={originalManagedUser?.uuid}
                            accountUuid={currentAccount.accountUUID}
                            initAllowedAccounts={currentAccount?.allowedAccounts ?? []}
                            changeAllowedAccounts={(val) => {
                              form.setFieldValue('allowedAccounts', val);
                            }}
                            openAllowedAccountsModal={openAllowedAccountsModal}
                            toggleAllowedAccountsModal={toggleAllowedAccountsModal}
                          />
                          : <div />
                      }
                    </>
                  )}
                </Field>
                {/* User Role Details Section */}
                <UserRoleDetailsSection
                  formik={{ dirty, ...formik }}
                  currentAccount={currentAccount}
                  backendErrors={backendErrors}
                />

                {/* User Permissions Section */}
                <UserPermissionSection
                  values={formik.values}
                  backendErrors={backendErrors}
                  setBackendErrors={setBackendErrors}
                  isOwnerAccDS={currentAccount.accountType === ACCOUNT_TYPES.DISTRIBUTION_SPONSOR}
                />

                {/* Allowed Accounts Section */}
                {currentAccount.accountType === ACCOUNT_TYPES.DISTRIBUTION_SPONSOR && (
                  <FormSection title={'Account Access'}>
                    <CheckBoxOption
                      optionKey={'manageChildAccounts'}
                      backendErrors={backendErrors}
                      setBackendErrors={setBackendErrors}
                    />
                    <AllowedAccountsSection
                      values={formik.values}
                      backendErrors={backendErrors}
                      toggleAllowedAccountsModal={toggleAllowedAccountsModal}
                    />
                  </FormSection>
                )}
              </Form>
            </UnsavedChangesGuard>
          );
        }}
      </Formik>

      <RemoveAccountModal
        open={isRemoveModalOpen}
        handleClose={handleCloseModal}
        accountName={currentAccount?.accountBusinessName || currentAccount?.name}
        onConfirm={handleConfirmRemove}
        isRemoving={isRemoving}
        isLastAccount={totalAccountsCount === 1}
      />

      {regularLayout && <div className={classes.actionsContainer}>
        <RoundedButton
          type="submit"
          variant="contained"
          color="primary"
          form="account-form"
          disabled={!isDirty}
          className={classes.actionButton}
        >
          {titles.saveChanges}
        </RoundedButton>
      </div>}
    </div>
  );
};

export default AccountContent;
